/**
 * React-Select 组件的样式配置
 * @param {boolean} isDark - 是否为暗色主题
 * @param {function} getThemeColor - 获取主题色的函数
 * @returns {Object} select 组件的样式配置对象
 */
export const getSelectStyles = (isDark, getThemeColor) => ({
	// 控制容器样式
	control: (base, state) => ({
		...base,
		minHeight: '2.5rem',
		backgroundColor: isDark ? getThemeColor('gray-1100') : getThemeColor('gray-100'),
		borderColor: state.isFocused ? getThemeColor('primary') : isDark ? getThemeColor('gray-700') : getThemeColor('gray-300'),
		boxShadow: state.isFocused ? `0 0 0 0.2rem ${getThemeColor('primary')}25` : 'none',
		'&:hover': {
			borderColor: getThemeColor('primary'),
		},
		transition: 'all .2s ease-in-out',
	}),

	// 选项容器样式
	menu: (base) => ({
		...base,
		backgroundColor: isDark ? getThemeColor('gray-1000') : getThemeColor('white'),
		zIndex: 9999,
		animation: 'fadeIn .1s ease-in-out',
	}),

	// 选项样式
	option: (base, { isFocused, isSelected }) => ({
		...base,
		backgroundColor: isSelected ? getThemeColor('primary') : isFocused ? (isDark ? getThemeColor('gray-900') : getThemeColor('gray-100')) : 'transparent',
		color: isSelected ? getThemeColor('white') : isDark ? getThemeColor('gray-200') : getThemeColor('gray-900'),
		cursor: 'pointer',
		':active': {
			backgroundColor: getThemeColor('primary'),
		},
	}),

	// 多选值容器样式
	multiValue: (base) => ({
		...base,
		backgroundColor: isDark ? getThemeColor('gray-900') : getThemeColor('gray-200'),
		margin: '2px',
	}),

	// 多选值标签样式
	multiValueLabel: (base) => ({
		...base,
		color: isDark ? getThemeColor('gray-200') : getThemeColor('gray-900'),
		padding: '0 6px',
	}),

	// 多选值删除按钮样式
	multiValueRemove: (base) => ({
		...base,
		color: isDark ? getThemeColor('gray-400') : getThemeColor('gray-600'),
		'&:hover': {
			backgroundColor: getThemeColor('danger'),
			color: getThemeColor('white'),
		},
	}),

	// 输入框样式
	input: (base) => ({
		...base,
		color: isDark ? getThemeColor('gray-200') : getThemeColor('gray-900'),
	}),

	// 占位符样式
	placeholder: (base) => ({
		...base,
		color: isDark ? getThemeColor('gray-500') : getThemeColor('gray-400'),
	}),

	// 无选项提示样式
	noOptionsMessage: (base) => ({
		...base,
		color: isDark ? getThemeColor('gray-400') : getThemeColor('gray-600'),
	}),
});

/** 订单列表筛选：控件对齐 form-select-sm，菜单样式与 theme react-select 一致 */
export const getOrderListSelectStyles = (isDark) => ({
	container: (base) => ({
		...base,
		width: '100%',
	}),
	control: (base, state) => ({
		...base,
		width: '100%',
		height: 'calc(1.5em + 0.5rem + calc(var(--bs-border-width, 1px) * 2))',
		minHeight: 'calc(1.5em + 0.5rem + calc(var(--bs-border-width, 1px) * 2))',
		maxHeight: 'calc(1.5em + 0.5rem + calc(var(--bs-border-width, 1px) * 2))',
		flexWrap: 'nowrap',
		fontSize: '0.875rem',
		lineHeight: 1.5,
		backgroundColor: 'var(--bs-form-control-bg)',
		borderColor: state.isFocused ? 'var(--falcon-input-focus-border-color-global, #86b7fe)' : isDark ? 'rgba(255, 255, 255, 0.16)' : 'var(--falcon-gray-300, #dee2e6)',
		borderRadius: '0.25rem',
		borderWidth: '1px',
		borderStyle: 'solid',
		boxShadow: state.isFocused
			? 'inset 0 1px 2px rgba(var(--bs-body-color-rgb), 0.075), 0 0 0 0.25rem rgba(var(--bs-primary-rgb), 0.25)'
			: isDark
				? 'inset 0 1px 2px rgba(0, 0, 0, 0.2)'
				: 'inset 0 1px 2px rgba(var(--bs-body-color-rgb), 0.075)',
		cursor: 'pointer',
		paddingTop: '0.25rem',
		paddingBottom: '0.25rem',
		paddingLeft: '0.5rem',
		paddingRight: 0,
		alignItems: 'center',
		overflow: 'hidden',
		transition: 'none',
	}),
	multiValue: (base) => ({
		...base,
		backgroundColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'var(--falcon-gray-200)',
		borderRadius: '0.25rem',
		border: isDark ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid var(--falcon-gray-300)',
		margin: 0,
	}),
	multiValueLabel: (base) => ({
		...base,
		fontSize: '12px',
		lineHeight: 1.25,
		padding: '0 0.35rem',
	}),
	multiValueRemove: (base) => ({
		...base,
		color: isDark ? 'var(--falcon-gray-400, #9da9bb)' : 'var(--falcon-gray-600)',
		borderRadius: '0 2px 2px 0',
		':hover': {
			backgroundColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'transparent',
			color: isDark ? '#fff' : 'var(--falcon-gray-900)',
		},
	}),
	valueContainer: (base) => ({
		...base,
		padding: 0,
		gap: '0.2rem',
		alignItems: 'center',
		flexWrap: 'nowrap',
		minWidth: 0,
		flex: '1 1 0',
	}),
	placeholder: (base) => ({
		...base,
		color: 'var(--bs-secondary-color)',
		fontSize: '0.875rem',
		margin: 0,
	}),
	input: (base) => ({
		...base,
		fontSize: '0.875rem',
		margin: 0,
	}),
	inputContainer: (base, state) => ({
		...base,
		margin: 0,
		padding: 0,
		flex: state.selectProps.menuIsOpen || state.isFocused ? '1 1 48px' : '0 1 2px',
		minWidth: state.selectProps.menuIsOpen || state.isFocused ? 48 : 2,
		maxWidth: state.selectProps.menuIsOpen || state.isFocused ? '100%' : 2,
	}),
	indicatorsContainer: (base) => ({
		...base,
		paddingRight: 0,
		flexShrink: 0,
	}),
	dropdownIndicator: (base) => ({
		...base,
		padding: 0,
	}),
	clearIndicator: (base) => ({
		...base,
		padding: '0 0.15rem',
	}),
	menuPortal: (base) => ({ ...base, zIndex: 9999 }),
	indicatorSeparator: () => ({ display: 'none' }),
	menu: (base) => ({
		...base,
		marginTop: 0,
		padding: 0,
		overflow: 'hidden',
		width: '100%',
		maxWidth: '100%',
	}),
	menuList: () => ({
		maxHeight: undefined,
		overflow: 'visible',
		padding: 0,
	}),
	option: (base) => ({
		...base,
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		maxWidth: '100%',
		cursor: 'pointer',
	}),
});
